/**
 * V2.0.78 — Wall-drawing tool extracted from FloorPlan2D.jsx.
 *
 * Owns the full state + click flow for the "+ Wall" toolbar tool:
 *   1. User clicks the tool button → `activate()` sets `active=true`
 *      and (via the passed callback) clears any lingering symbol
 *      selection so the previous highlight doesn't ghost behind the
 *      drawing overlay.
 *   2. Left-click on the canvas → `handleClick()` records the start
 *      point (snapped). Second click commits the segment to
 *      `floor.walls[]` and chains — last endpoint becomes the next
 *      start so users can lay out a whole partition run in one flow.
 *   3. Mouse move → `updatePreview()` follows the cursor so the dashed
 *      preview line lands where the user expects.
 *   4. Escape with an in-flight draft → clear the draft but stay in
 *      the tool. Escape with no draft → exit the tool entirely.
 *
 * Persisted schema (matches the 3D BIMPrimitives + IFC + validator
 * expectations):
 *   { id, start:[x,z], end:[x,z], x1,y1,x2,y2,
 *     height, thickness, type:"interior", kind:"interior",
 *     material:"wood_frame", user_drawn:true }
 *
 * The extraction target here is intentionally small (~150 lines) so we
 * can validate the refactor pattern before touching the higher-risk
 * chunks of FloorPlan2D (wall-add menu, symbol drag, opening drag).
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { snap as snapToGrid } from "../../lib/units";
import { emitInteractionEvent } from "../../lib/interactionTelemetry";

export default function useWallDrawTool({
  blueprint,
  floorIndex,
  measurementSystem,
  pointerToWorld,
  onUpdateBlueprint,
  pushHistory,
  onActivate,
}) {
  const [active, setActive] = useState(false);
  const [draft, setDraft] = useState(null); // { start: {x_m, z_m} } | null
  const [preview, setPreview] = useState(null); // {x_m, z_m}
  // V2.0.81 — telemetry: track when the tool was activated so we can
  // report "session duration" on exit + count how many walls landed.
  const sessionRef = useRef(null); // { startMs, wallsPlaced }

  const exit = useCallback(() => {
    // Emit a summary event for the session that just ended.
    if (sessionRef.current) {
      emitInteractionEvent({
        tool: "wall_draw",
        action: "session_end",
        ms: Date.now() - sessionRef.current.startMs,
        committed: sessionRef.current.wallsPlaced > 0,
        meta: { walls_placed: sessionRef.current.wallsPlaced },
      });
      sessionRef.current = null;
    }
    setActive(false);
    setDraft(null);
    setPreview(null);
  }, []);

  const activate = useCallback(() => {
    onActivate?.();
    setActive(true);
    sessionRef.current = { startMs: Date.now(), wallsPlaced: 0 };
    emitInteractionEvent({ tool: "wall_draw", action: "activate" });
  }, [onActivate]);

  const toggle = useCallback(() => {
    if (active) exit();
    else activate();
  }, [active, activate, exit]);

  const updatePreview = useCallback(
    (e) => {
      if (!active) return;
      const world = pointerToWorld(e);
      if (!world) return;
      const snap = (v) => snapToGrid(v, measurementSystem);
      setPreview({ x_m: snap(world.x_m), z_m: snap(world.z_m) });
    },
    [active, pointerToWorld, measurementSystem],
  );

  const commitWall = useCallback(
    (startPt, endPt) => {
      if (!blueprint) return;
      pushHistory?.();
      const next = JSON.parse(JSON.stringify(blueprint));
      const f = (next.floors || [])[floorIndex - 1];
      if (!f) return;
      if (!Array.isArray(f.walls)) f.walls = [];
      const height = Number(f.ceiling_height) || 2.7;
      const id = `user_wall_${Date.now().toString(36)}_${Math.random()
        .toString(36)
        .slice(2, 6)}`;
      f.walls.push({
        id,
        start: [startPt.x_m, startPt.z_m],
        end: [endPt.x_m, endPt.z_m],
        x1: startPt.x_m,
        y1: startPt.z_m,
        x2: endPt.x_m,
        y2: endPt.z_m,
        height,
        thickness: 0.12,
        type: "interior",
        kind: "interior",
        material: "wood_frame",
        user_drawn: true,
      });
      onUpdateBlueprint?.(next);
      // V2.0.81 — telemetry per commit + bump session counter for the
      // exit summary event.
      if (sessionRef.current) sessionRef.current.wallsPlaced += 1;
      emitInteractionEvent({
        tool: "wall_draw",
        action: "wall_committed",
        committed: true,
      });
    },
    [blueprint, floorIndex, pushHistory, onUpdateBlueprint],
  );

  const handleClick = useCallback(
    (e) => {
      const world = pointerToWorld(e);
      if (!world) return;
      const snap = (v) => snapToGrid(v, measurementSystem);
      const x_m = snap(world.x_m);
      const z_m = snap(world.z_m);
      if (!draft) {
        setDraft({ start: { x_m, z_m } });
        setPreview({ x_m, z_m });
        return;
      }
      const dx = x_m - draft.start.x_m;
      const dz = z_m - draft.start.z_m;
      if (Math.hypot(dx, dz) < 0.1) {
        // Same-spot click — cancel this segment but stay in tool.
        setDraft(null);
        setPreview(null);
        return;
      }
      commitWall(draft.start, { x_m, z_m });
      // Chain: end becomes next start.
      setDraft({ start: { x_m, z_m } });
      setPreview({ x_m, z_m });
    },
    [draft, pointerToWorld, measurementSystem, commitWall],
  );

  const deleteWall = useCallback(
    (e, wallId) => {
      e.stopPropagation();
      if (active) return; // don't delete while placing new ones
      if (!blueprint) return;
      pushHistory?.();
      const next = JSON.parse(JSON.stringify(blueprint));
      const f = (next.floors || [])[floorIndex - 1];
      if (!f || !Array.isArray(f.walls)) return;
      f.walls = f.walls.filter((w) => w.id !== wallId);
      onUpdateBlueprint?.(next);
    },
    [active, blueprint, floorIndex, pushHistory, onUpdateBlueprint],
  );

  // Escape handler: clears in-flight draft first, then exits on a
  // second Escape. Only bound while the tool is active.
  useEffect(() => {
    if (!active) return;
    const onKey = (e) => {
      if (e.key !== "Escape") return;
      if (draft) {
        setDraft(null);
        setPreview(null);
      } else {
        exit();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [active, draft, exit]);

  return {
    active,
    draft,
    preview,
    activate,
    exit,
    toggle,
    handleClick,
    updatePreview,
    deleteWall,
  };
}
